import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap } from 'rxjs/operators';

import { injectCatchHttpError } from '../../../../core/errors/catch-http-error.util';
import { CoreHelperService } from '../../../../core/services/helpers/core-helper.service';
import { ApiSearchService } from '../../../../search/services/api-search.service';
import { waitForRequestsToDrain } from '../../../../shared/interceptors/wait-all-requests.interceptor';
import { ApiMonitoringService } from '../../../services/api/api-monitoring.service';
import * as monitoringActions from './monitoring.actions';

@Injectable()
export class MonitoringEffects {
  private helper = inject(CoreHelperService);
  private apiSearch = inject(ApiSearchService);
  private apiMonitoring = inject(ApiMonitoringService);

  private readonly actions$ = inject<Actions>(Actions);
  private readonly catchHttpError = injectCatchHttpError();

  getMonitoringTopics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(monitoringActions.loadTopics),
      switchMap(({ params }) =>
        this.apiMonitoring.getMonitoringTopics(params).pipe(
          map(topics => monitoringActions.loadTopicsSuccess({ topics })),
          this.catchHttpError(() => monitoringActions.loadTopicsFail()),
        ),
      ),
    ),
  );

  getIrrelevantItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(monitoringActions.loadIrrelevantItems),
      switchMap(data => waitForRequestsToDrain().pipe(map(() => data))),
      switchMap(({ params, shouldAddToList }) =>
        this.apiMonitoring.getIrrelevantItems(params).pipe(
          map(response => monitoringActions.loadIrrelevantItemsSuccess({ data: response, shouldAddToList })),
          this.catchHttpError(() => monitoringActions.loadIrrelevantItemsFail()),
        ),
      ),
    ),
  );

  getMonitoringItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(monitoringActions.loadMonitoringItems),
      switchMap(data => waitForRequestsToDrain().pipe(map(() => data))),
      switchMap(({ params, shouldAddToList }) =>
        this.apiMonitoring.getMonitoringItems(params).pipe(
          map(response => monitoringActions.loadMonitoringItemsSuccess({ data: response, shouldAddToList })),
          this.catchHttpError(() => monitoringActions.loadMonitoringItemsFail()),
        ),
      ),
    ),
  );

  getSearchItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(monitoringActions.loadSearchItems),
      switchMap(({ params, shouldAddToList }) => {
        const sParams = this.helper.formatters.convertObjToQueryString(params);
        return this.apiSearch.getSearchItems(sParams).pipe(
          map(response => monitoringActions.loadSearchItemsSuccess({ data: response, shouldAddToList })),
          this.catchHttpError(() => monitoringActions.loadSearchItemsFail()),
        );
      }),
    ),
  );

  getCurrentTopic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(monitoringActions.loadCurrentTopic),
      switchMap(({ id }) =>
        this.apiMonitoring.getMonitoringTopic(id).pipe(
          map(topic => monitoringActions.loadCurrentTopicSuccess({ topic })),
          this.catchHttpError(error => monitoringActions.loadCurrentTopicFail({ error })),
        ),
      ),
    ),
  );
}
